import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { Link } from "gatsby";
import Markdown from "markdown-to-jsx";

import styles from "./VenueCard.module.scss";
import { Title } from "../Title";
import { getOpenLabel, OpenStatus } from "../OpenLabel";
import { Expandable } from "../Expandable";
import { Icon } from "../Icon";
import { FramedImage, FramePathList } from "../FramedImage";
import { CreativeMarkdown } from "../CreativeMarkdown";

interface VenueCardProps {
  className?: string;
  name: string;
  tags: string[];
  status: OpenStatus;
  active?: boolean;
  number: number;
  slug: string;
  children: string;
  image?: {
    publicURL: string;
  };
  display?: boolean;
  onInteract?: (expanded: boolean) => void;
}

const frames: FramePathList[] = [
  "RoundTopOne",
  "RoundTopTwo",
  "RoundTopThree",
  "RoundTopFour",
  "RoundTopFive",
  "RoundTopSix",
];

const VenueCard: React.FC<VenueCardProps> = ({
  className,
  name,
  tags,
  status,
  active,
  children,
  number,
  slug,
  image,
  onInteract,
  display = true,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [internalDisplay, setDisplay] = useState(false);

  useEffect(() => {
    if (onInteract) {
      onInteract(expanded);
    }
  }, [onInteract, expanded]);

  useEffect(() => {
    if (display) {
      const timer = setTimeout(() => {
        setDisplay(true);
      }, number * 100);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display]);

  return (
    <div
      className={classNames(
        styles.card,
        { [styles.cardOpen]: expanded },
        { [styles.display]: internalDisplay },
        className
      )}
    >
      <Link to={slug} className={styles.image}>
        {image && (
          <FramedImage
            src={image.publicURL}
            pathName={frames[number % frames.length]}
          />
        )}
      </Link>
      <div className={styles.info}>
        <div
          role="button"
          onClick={() => setExpanded(!expanded)}
          className={styles.top}
        >
          <div className={styles.row}>
            <Title className={styles.title} size="small" stable>
              {name}
            </Title>
            <span className={styles.number}>{number}</span>
          </div>
          <div className={styles.row}>
            <ul className={styles.tags}>
              {tags.slice(0, 3).map(tag => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>
          </div>
          <div className={styles.row}>
            <div>
              <span className={classNames(styles.label, styles[status])}>
                {getOpenLabel(status)}
              </span>
            </div>
            <Icon className={styles.icon} name="chevron-down" size="large" />
          </div>
        </div>
        <Expandable open={expanded}>
          <div className={styles.description}>
            {/* <Title stable tagName="p" className="color-text">
              What's on
            </Title> */}
            <CreativeMarkdown>{children}</CreativeMarkdown>
            <Link className={styles.more} to={slug}>
              Find out more
            </Link>
          </div>
        </Expandable>
      </div>
    </div>
  );
};

export { VenueCard };
